import React from 'react';
import styled from 'styled-components';

import { getScoreColors } from 'modules/health/helpers/getScoreColors';
import { toBarHeight } from 'modules/health/helpers/toBarHeight';

import { MonthsData } from './MonthsStatsCard';

interface BarChartProps {
  monthsData: MonthsData[];
}

export const BarChart = ({ monthsData }: BarChartProps) => {
  return (
    <>
      <TracksRow>
        <DashedLine $top={50} />
        {monthsData.map((d) => {
          const actualH = toBarHeight(d.actualPercent);
          const [color, borderColor] = getScoreColors(d.actualPercent);

          return (
            <Track key={d.month}>
              <ActualBar
                $height={actualH}
                $color={color}
                $borderColor={borderColor}
              />
            </Track>
          );
        })}
        <YAxisContainer>
          <YAxisLabel $top={0}>100</YAxisLabel>
          <YAxisLabel $top={50}>50</YAxisLabel>
        </YAxisContainer>
      </TracksRow>

      <LabelsRow>
        {monthsData.map((d) => (
          <MonthLabel key={d.month}>{d.month}</MonthLabel>
        ))}
      </LabelsRow>
    </>
  );
};

const TracksRow = styled.div`
  position: relative;
  height: 100px;
  display: flex;
  align-items: stretch;
  gap: 8px;
  padding-right: 5px;
`;

const LabelsRow = styled.div`
  display: flex;
  gap: 8px;
  padding-right: 5px;
  padding-top: 5px;
`;

const YAxisContainer = styled.div`
  position: absolute;
  right: 0;
  top: 0;
  bottom: 0;
  width: 15px;
`;

const YAxisLabel = styled.span<{ $top: number }>`
  position: absolute;
  left: 0;
  top: ${({ $top }) => $top}%;
  transform: ${({ $top }) =>
    $top === 0 ? 'translateY(0)' : 'translateY(-50%)'};
  font-weight: 700;
  font-size: 10px;
  line-height: 20px;
  text-align: right;
`;

const DashedLine = styled.div<{ $top: number }>`
  position: absolute;
  left: 0;
  right: 15px;
  top: ${({ $top }) => $top}%;
  border: 1px dashed #d9d9d9;
  z-index: 1;
  pointer-events: none;
  transform: translateY(-50%);
`;

const Track = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  align-items: center;
  position: relative;
  height: 100px;
  box-sizing: border-box;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 3px;
    background: #d9d9d9;
    border-radius: 3px;
  }
`;

const ActualBar = styled.div<{
  $height: number;
  $color: string;
  $borderColor: string;
}>`
  width: 9px;
  height: ${({ $height }) => $height}%;
  background: ${({ $color }) => $color};
  border-radius: 5px;
  z-index: 2;
  border: 1px solid ${({ $borderColor }) => $borderColor};
  box-shadow: 0 1px 5px 0 #0003;
  box-sizing: border-box;
`;

const MonthLabel = styled.span`
  flex: 1;
  font-size: 12px;
  line-height: 20px;
  text-align: center;
  color: #414e7266;
`;
